import MapaViewer, { SeatTogglePayload } from "@/components/mapa/MapaViewer";
import { MapaSkeleton } from "@/components/mapa/MapaSkeleton";
import { TipoEntradaLeyenda } from "./TipoEntradaLeyenda";
import { SelectorEntradasMapa } from "./SelectorEntradasMapa";
import { useEvento } from "@/store/evento/evento";
import { useEventoContext } from "../../contexts/eventos";
import { useOccupiedSeats } from "../../hooks/useOccupiedSeats";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
    funcionId: string;
    maxEntradas: boolean;
    onSeatToggle: (evt: SeatTogglePayload) => void;
}

/**
 * Layout para eventos con mapa de asientos.
 * Muestra: leyenda arriba, mapa completo.
 */
export function LayoutConMapa({
    funcionId,
    maxEntradas,
    onSeatToggle
}: Props) {
    const { mapa, tipos } = useEventoContext();
    const { state: entradas } = useEvento.use.entradas();
    const [fullscreen, setFullscreen] = useState(false);

    const openFullscreen = useCallback(() => {
        setFullscreen(true);
        try { (screen.orientation as any).lock('portrait').catch(() => { }); } catch { }
    }, []);

    const closeFullscreen = useCallback(() => {
        setFullscreen(false);
        try { screen.orientation.unlock(); } catch { }
    }, []);

    // Unlock orientation on unmount
    useEffect(() => {
        return () => { try { screen.orientation.unlock(); } catch { } };
    }, []);

    // Fetch occupied seats for this specific function
    const { seats: occupiedSeatsRaw, blockedSeats, loading: loadingSeats } = useOccupiedSeats(funcionId);

    // Bloqueados/cortesía se tratan igual que ocupados: grises, no clickeables
    const occupiedSeats = useMemo(
        () => [...occupiedSeatsRaw, ...blockedSeats],
        [occupiedSeatsRaw, blockedSeats]
    );

    const conflictSeats = useMemo(() => {
        if (!entradas || entradas.length === 0 || occupiedSeats.length === 0) return [];

        const occupiedSet = new Set(occupiedSeats);
        const conflicts: string[] = [];

        entradas.forEach((entrada) => {
            entrada.asientos?.forEach((asiento) => {
                if (occupiedSet.has(asiento.seatId)) {
                    conflicts.push(asiento.seatId);
                }
            });
        });

        return conflicts;
    }, [entradas, occupiedSeats]);

    const selectedSeats = useMemo(() => {
        if (!entradas || entradas.length === 0) return [];

        const seats: string[] = [];
        entradas.forEach((entrada) => {
            entrada.asientos?.forEach((asiento) => {
                seats.push(asiento.seatId);
            });
        });

        return seats;
    }, [entradas]);

    return (
        <>
            <div className="flex flex-col gap-4">
                {/* Leyenda de precios */}
                <div className="w-full">
                    <TipoEntradaLeyenda />
                </div>

                {/* Mobile: button to open fullscreen map (hidden for now) */}
                <button
                    onClick={() => openFullscreen()}
                    className="hidden items-center justify-center gap-2 w-full py-3 bg-brand-red/10 border border-[#ff000014] rounded-lg text-white font-medium text-sm hover:bg-brand-red/20 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                    Ver mapa completo
                </button>

                {/* Mapa */}
                {loadingSeats ? (
                    <MapaSkeleton />
                ) : (
                    <MapaViewer
                        key={funcionId}
                        initialMapa={mapa!}
                        initialTipos={tipos}
                        occupiedSeats={occupiedSeats}
                        conflictSeats={conflictSeats}
                        selectedSeats={selectedSeats}
                        onSeatToggle={onSeatToggle}
                        disabled={maxEntradas}
                    />
                )}

                {/* Mobile: ticket selector with +/- buttons */}
                <SelectorEntradasMapa
                    occupiedSeats={occupiedSeats}
                    onSeatToggle={onSeatToggle}
                />
            </div>

            {/* Fullscreen map overlay (mobile) */}
            {fullscreen && (
                <div className="fixed inset-0 z-[100] bg-black">
                    {/* Floating close button (stays in normal orientation) */}
                    <button
                        onClick={() => closeFullscreen()}
                        className="absolute top-4 right-4 z-[101] text-white bg-brand-red hover:bg-[#c0392b] rounded-full p-2 shadow-lg transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    {/* Map container - rotated to landscape, fits full height */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div
                            style={{
                                width: 'calc(100vh - 2rem)',
                                height: 'calc(100vw - 2rem)',
                                transform: 'rotate(90deg)',
                            }}
                        >
                            {!loadingSeats && (
                                <MapaViewer
                                    key={`fullscreen-${funcionId}`}
                                    initialMapa={mapa!}
                                    initialTipos={tipos}
                                    occupiedSeats={occupiedSeats}
                                    conflictSeats={conflictSeats}
                                    selectedSeats={selectedSeats}
                                    onSeatToggle={onSeatToggle}
                                    disabled={maxEntradas}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
